<template>
    <div class="space-y-2">
        <button
            data-test-id="pdp-upload-button"
            type="button"
            :disabled="isUploading || isRedirecting || disabled"
            class="inline-flex h-14 w-full cursor-pointer items-center justify-center gap-x-1 rounded-lg bg-order-buttons px-3.5 text-base font-extrabold text-white transition hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-2"
            :class="{ 'cursor-not-allowed opacity-75': isUploading || isRedirecting }"
            @click="openFileDialog()"
        >
            <Spinner v-if="isUploading || isRedirecting" size="sm" size-override="size-6 md:size-6" class="mr-2.5" for-button />
            <AnimatedUploadIcon v-if="!isUploading && !isRedirecting" aria-hidden="true" class="mr-1.5 size-6" />
            {{ addToCartButtonLabel }}
        </button>

        <ActionStatus
            kind="upload"
            :state="overlay.state.value"
            :progress="overlay.displayProgress.value"
            :error-message="overlay.errorMessage.value"
            :is-redirecting="isRedirecting"
            @retry="onRetry"
            @close="overlay.clear()"
        />

        <UploadLeaveModal
            :open="leaveGuard.isOpen.value"
            @confirm="leaveGuard.onConfirm"
            @cancel="leaveGuard.onCancel"
            @update:open="leaveGuard.onUpdateOpen"
        />
    </div>
</template>

<script setup lang="ts">
import Spinner from '@/components/spinners/Spinner.vue';
import { useUploadLeaveGuard } from '@/composables/product-page/useUploadLeaveGuard';
import { useUploadStatusController } from '@/composables/product-page/useUploadStatusController';
import { useToolPagePropsComposable } from '@/composables/tools/mediaclip/UseToolPagePropsComposable';
import { useDirectFileUpload } from '@/composables/useDirectFileUpload';
import { useConnectionGuard } from '@/composables/useConnectionGuard';
import { useUploadWakeLock } from '@/composables/useUploadWakeLock';
import AnimatedUploadIcon from '@/icons/AnimatedUploadIcon.vue';
import ActionStatus from '@/components/status/ActionStatus.vue';
import UploadLeaveModal from '@/pages/product-page/partials/product-info/partials/UploadLeaveModal.vue';
import { useProductStore } from '@/stores/products/ProductStore';
import { useMediaclipSessionStore } from '@/stores/tool/MediaclipSessionStore';
import type { ConfigurableProductFilterOptionObject } from '@/types/generated';
import MediaclipProductToolConfiguratorController from '@/types/wayfinder/actions/App/Http/Controllers/Web/Tools/Mediaclip/MediaclipProductToolConfiguratorController';
import { router } from '@inertiajs/vue3';
import { useFileDialog } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

type ButtonProps = {
    disabled?: boolean;
};

withDefaults(defineProps<ButtonProps>(), {
    disabled: false,
});

const productStore = useProductStore();
const { t } = useI18n();

// Configured per magento group / configurable product in Joost; falls back to
// the per-locale default when nothing is set. The same field also drives the
// add-to-cart button label for collection products (ProductCollectionButton.vue,
// ProductOptionsModalOverview.vue) — one field for every "start the purchase" CTA.
const addToCartButtonLabel = computed<string>(
    () => productStore.configurableProduct.add_to_cart_button_label || t('components.buttons.default.upload_photo.label'),
);

const { isUploading, uploadProgress, uploadFile, prefetchSession, cancelUpload, onUploadError } = useDirectFileUpload();

const isRedirecting = ref<boolean>(false);
const isSelfInitiatedRedirect = ref<boolean>(false);

const overlay = useUploadStatusController(uploadProgress);

const { open, onChange } = useFileDialog({
    accept: 'image/png,image/jpg,image/jpeg,image/webp',
    multiple: false,
    directory: false,
    reset: true,
});

// The upload session is requested while the user is still picking a file, so the round trip
// is done by the time the transfer can start.
function openFileDialog(): void {
    prefetchSession();
    open();
}

const leaveGuard = useUploadLeaveGuard({
    isActive: () => isUploading.value || isRedirecting.value,
    isSelfInitiated: () => isSelfInitiatedRedirect.value,
    markSelfInitiated: () => {
        isSelfInitiatedRedirect.value = true;
    },
    onLeave: () => {
        cancelUpload();
        overlay.clear();
    },
});

useConnectionGuard({
    isWaiting: isUploading,
    isShowingLoading: overlay.isShowingLoading,
    isShowingOffline: overlay.isShowingOffline,
    onOffline: overlay.showOffline,
    onReconnected: overlay.resumeLoading,
});

useUploadWakeLock({
    isActive: () => isUploading.value || isRedirecting.value,
});

onUploadError(failure => {
    overlay.showError(failure.message);
});

onChange(async files => {
    const file = files?.[0];

    if (!file) {
        return;
    }

    await startUploadFlow(file);
});

async function startUploadFlow(file: File): Promise<void> {
    overlay.showLoading();

    // Apply the cutout filter based on image orientation first so that
    // mostPopularProduct (the filter default) is already resolved before
    // we pick the upload SKU. This ensures the Mediaclip project is created
    // for the same product that the UI will keep selected after the redirect.
    const cutoutFilterOption = await productStore.determineAndApplyImageOrientation(file);
    const sku =
        cutoutFilterOption && productStore.mostPopularProduct
            ? productStore.mostPopularProduct.product.sku
            : productStore.selectedProduct.product.sku;

    const result = await uploadFile(file, sku).catch(() => null);

    if (!result?.mediaclip_project_id) {
        return;
    }

    await redirectToConfigurator(result.mediaclip_project_id, cutoutFilterOption);
}

async function redirectToConfigurator(
    mediaclipProjectId: string,
    cutoutFilterOption: ConfigurableProductFilterOptionObject | undefined,
): Promise<void> {
    isRedirecting.value = true;
    isSelfInitiatedRedirect.value = true;

    const redirectHref = MediaclipProductToolConfiguratorController({
        mediaclipProjectId,
        configurableProduct: productStore.configurableProduct.url_key,
    }).url;

    router.visit(redirectHref, {
        onSuccess: () => applyCutoutFilterIfAvailable(cutoutFilterOption),
        onFinish: finalizeConfiguratorNavigation,
    });
}

function applyCutoutFilterIfAvailable(cutoutFilterOption: ConfigurableProductFilterOptionObject | undefined): void {
    if (!cutoutFilterOption) {
        return;
    }

    if (!productStore.mostPopularProduct) {
        return;
    }

    productStore.selectedProduct = productStore.mostPopularProduct;
}

function finalizeConfiguratorNavigation(): void {
    const { mediaclipProject } = useToolPagePropsComposable();

    if (!mediaclipProject.value) {
        return;
    }

    useMediaclipSessionStore().activate(mediaclipProject.value.mediaclip_project_id, productStore.selectedProduct.product.sku);
    isRedirecting.value = false;
    isSelfInitiatedRedirect.value = false;
    overlay.clear();
}

function onRetry(): void {
    overlay.clear();
    openFileDialog();
}
</script>
